import { createColumnHelper, Row } from '@tanstack/react-table';
import { OverflowColumn } from '@monorepo/base/src/components/table/overflow-column/overflow-column';
import { isSubRow } from '@monorepo/base/src/components/table/table';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import { JobModel } from '../../models/job.model';
import routes from '../../routes/index.routes';
import { useJobActions } from '../actions/job.actions';
import { IMergedHeadersReturn } from './index.headers';
import { SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { QuickActionsMenu } from '@monorepo/base/src/components/table/quick-actions-menu/quick-actions-menu';
import { TableMergeColumn } from '@monorepo/base/src/components/table/table-merge-column/table-merge-column';
import { TableSelectionCell, TableSelectionHeader } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { IAdminxStore } from '../../stores';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useEffect } from 'react';
import { Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { PerformanceResponse } from '../../models/performance.model';

const columnHelper = createColumnHelper<JobModel>();

export const useJobHeaders = (isReportStore: boolean): IMergedHeadersReturn<JobModel> => {
	const { settingsStore, jobStore } = useStores<IAdminxStore>();
	const { EntityActions } = useJobActions();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const { dispatchLog } = useLogs();

	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;
	const reportList = jobStore.getListStore();
	const noReportsJobsStore = jobStore.getJobsListStore();

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				id: true,
				name: true,
				taskClasses: true,
				cronExpression: true,
				isActive: true,
				runOnInit: true,
				maxRetries: true,
				retryTimeout: true,
				refreshRate: true,

				startTime: true,
				jobIdentifier: false,
				status: false,
				retryAttempts: false,
				errorMessage: false,
				endTime: false,
				jobDuration: false,
				nextRunTime: false,
			});
			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());
		}
	}, [tableStore]);

	const handleToggleUpdate = (row: Row<JobModel>, val: boolean, modelKey: string, reportKey: string) => {
		const id = row.getValue('id') as id;

		jobStore.getCrud().setData(new JobModel(row.original));

		jobStore.getCrud().apiLayer.edit(id, {
			...jobStore.getCrud().getEditFormData(),
			[modelKey]: val,
		});

		if (!isReportStore) {
			const updatedJobList = noReportsJobsStore.getList().map(job => {
				if (job.id === id) {
					return new JobModel({ ...job, [reportKey]: val });
				}
				return job;
			});
			noReportsJobsStore.setList(updatedJobList);
		} else {
			reportList?.setData({
				...reportList?.getData(),
				data: reportList?.getData()?.data?.map(r => {
					if (r.id !== id) {
						return r;
					}

					return { ...r, [reportKey]: val };
				}),
			} as PerformanceResponse);
		}

		dispatchLog({
			msg: `Job ${row.getValue('name')} was updated`,
			type: DispatchLogsTypes.Success,
		});
	};

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				enableHiding: false,
				header: headerProps => <TableSelectionHeader headerProps={headerProps} />,
				cell: cellProps => {
					const { row } = cellProps;
					const job = new JobModel(row.original);

					if (isSubRow(row)) {
						return <span></span>;
					}
					if (!EntityActions) {
						return null;
					}
					return (
						<TableMergeColumn>
							<TableSelectionCell cellProps={cellProps} />
							<SpacerX x={0.7} />
							<QuickActionsMenu>
								<EntityActions entity={job} />
							</QuickActionsMenu>
						</TableMergeColumn>
					);
				},
			}),
			columnHelper.accessor('id', {
				header: 'ID',
			}),
			columnHelper.accessor('name', {
				header: 'Name',
				cell: info => {
					const { row } = info;
					return (
						<TableLink
							toEdit={routes.jobs.edit(row.getValue('id'))}
							toEditState={{ isBlockRequest: true } as BetweenPagesStateProps}>
							{row.getValue('name')}
						</TableLink>
					);
				},
			}),

			columnHelper.accessor('taskClasses', {
				header: 'Task',
				cell: info => {
					const { row } = info;
					return <OverflowColumn data={row.getValue('taskClasses')} />;
				},
			}),

			columnHelper.accessor('cronExpression', {
				header: 'Cron Expression',
			}),
			columnHelper.accessor('isActive', {
				header: 'Active',
				cell: ({ row }) => (
					<Toggle
						onChange={val => handleToggleUpdate(row, val, 'is_active', 'isActive')}
						defaultValue={row?.getValue('isActive')}
					/>
				),
			}),
			columnHelper.accessor('runOnInit', {
				header: 'Run On Init',
				cell: ({ row }) => (
					<Toggle
						onChange={val => handleToggleUpdate(row, val, 'run_on_init', 'runOnInit')}
						defaultValue={row?.getValue('runOnInit')}
					/>
				),
			}),
			columnHelper.accessor('maxRetries', {
				header: 'Max Retries',
			}),
			columnHelper.accessor('retryTimeout', {
				header: 'Retry Timeout',
			}),
			columnHelper.accessor('refreshRate', {
				header: 'Refresh Rate',
			}),

			columnHelper.accessor('startTime', {
				header: 'Start time',
				cell: ({ row }) => {
					const Content = () => (
						<div style={{ textAlign: 'left' }}>
							<div>
								Identifier: <b>{row.getValue('jobIdentifier')}</b>
							</div>
							<div>
								Start time: <b>{row.getValue('startTime')}</b>
							</div>
							<div>
								Finish time: <b>{row.getValue('endTime')}</b>
							</div>
							<div>
								Last duration: <b>{row.getValue('jobDuration')}</b>
							</div>
							<div>
								Status: <b>{row.getValue('status')}</b>
							</div>
							<div>
								Next run time: <b>{row.getValue('nextRunTime')}</b>
							</div>
						</div>
					);

					return (
						<Tooltip content={<Content />} isHoveredOnTooltip>
							<div>{row.getValue('startTime')}</div>
						</Tooltip>
					);
				},
			}),
			columnHelper.accessor('jobIdentifier', { header: 'Job Identifier' }),
			columnHelper.accessor('status', { header: 'Status' }),
			columnHelper.accessor('retryAttempts', { header: 'Retry Attempts' }),
			columnHelper.accessor('errorMessage', { header: 'Error Message' }),
			columnHelper.accessor('endTime', { header: 'End Time' }),
			columnHelper.accessor('jobDuration', { header: 'Job Duration' }),
			columnHelper.accessor('nextRunTime', { header: 'Next Run Time' }),
		],
	};
};
